import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  Check,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Smartphone,
} from 'lucide-react'
import {
  EMPTY_ESCALATION_PROFILE,
  getEscalationProfile,
  putEscalationProfile,
  type EscalationChannel,
  type EscalationProfile,
} from '../lib/api'
import { SocShell } from '../components/soc/SocShell'
import { OwnerMobileSection } from '../components/OwnerMobileSection'
import { WhoWeWakeSection } from '../components/WhoWeWakeSection'
import { BusinessClockSection } from '../components/BusinessClockSection'
import { timeAgo } from '../lib/utils'

/**
 * Escalation & contact preferences — "who gets woken, for what, and how"
 * (PRODUCT_VISION: "Escalation & contact preferences visible and adjustable —
 * client feels in control of how the team reaches them"; the onboarding
 * interview's "Can my human analyst call you at 3am if it's serious?").
 *
 * This page is about trust at 3am, so the voice is calm and precise — no
 * marketing adjectives, no default-on trickery, no masking games on the phone
 * number. The client sets exactly how the team reaches them and can see it.
 *
 * Backend: GET/PUT /escalation/profile (app/routers/escalation.py). A first
 * visit returns an empty shape (or 404) → a blank form, never an error page.
 */

// E.164, mirroring the backend's regex (app/services/sms.py _E164_RE): a
// leading '+', a non-zero first digit, then 6–14 more digits. We validate the
// same rule client-side so the client gets an honest inline error before the
// round-trip — not a masked or auto-"fixed" number.
const E164_RE = /^\+[1-9]\d{6,14}$/

// Plain-language anchors for the severity threshold, so "8" means something.
// Only a few points are anchored; the rest inherit the nearest phrasing.
function severityAnchor(level: number): string {
  if (level >= 9) return 'Only a confirmed, serious breach.'
  if (level >= 8) return 'Confirmed breach only.'
  if (level >= 6) return 'Serious findings your analyst has confirmed.'
  if (level >= 5) return 'Anything your analyst thinks needs your eyes today.'
  if (level >= 3) return 'Most things worth a mention, confirmed or not.'
  return 'Everything: you want to hear it all.'
}

interface ChannelOption {
  value: EscalationChannel
  label: string
  description: string
  icon: typeof Mail
}

// One honest line each — what the channel actually is, including its limits.
const CHANNELS: ChannelOption[] = [
  {
    value: 'app',
    label: 'In-app',
    description: 'A notification when you next open DigitalWatch.',
    icon: Smartphone,
  },
  {
    value: 'sms',
    label: 'SMS',
    description:
      'A text to the number above. It works even if your email is compromised.',
    icon: MessageSquare,
  },
  {
    value: 'email',
    label: 'Email',
    description: 'An email: convenient, but not if your mailbox is the target.',
    icon: Mail,
  },
]

export function EscalationPrefsPage() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Form fields (the editable subset of the profile).
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [threshold, setThreshold] = useState(
    EMPTY_ESCALATION_PROFILE.notify_severity_threshold,
  )
  const [threeAm, setThreeAm] = useState(false)
  const [channel, setChannel] = useState<EscalationChannel>('app')

  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const applyProfile = useCallback((p: EscalationProfile) => {
    setName(p.escalation_contact_name ?? '')
    setPhone(p.escalation_contact_phone_e164 ?? '')
    setThreshold(p.notify_severity_threshold)
    setThreeAm(p.three_am_call_consent)
    setChannel(p.preferred_channel)
    setSavedAt(p.exists ? p.updated_at : null)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const profile = await getEscalationProfile()
      applyProfile(profile)
    } catch {
      setLoadError(
        'We couldn’t load your escalation preferences right now.',
      )
    } finally {
      setLoading(false)
    }
  }, [applyProfile])

  useEffect(() => {
    void load()
  }, [load])

  const anchor = useMemo(() => severityAnchor(threshold), [threshold])

  const handleSave = useCallback(async () => {
    if (saving) return
    setSaveError(null)
    setSavedAt(null)

    // Normalise only whitespace (the helper shows a spaced example) — never
    // silently rewrite the digits. Validate the E.164 rule before any request.
    const cleanedPhone = phone.replace(/\s+/g, '')
    if (cleanedPhone && !E164_RE.test(cleanedPhone)) {
      setPhoneError(
        'That doesn’t look like a valid number. Use international format, e.g. +6421555123.',
      )
      return
    }
    setPhoneError(null)

    const trimmedName = name.trim()
    setSaving(true)
    try {
      const saved = await putEscalationProfile({
        escalation_contact_name: trimmedName || null,
        escalation_contact_phone_e164: cleanedPhone || null,
        notify_severity_threshold: threshold,
        three_am_call_consent: threeAm,
        preferred_channel: channel,
      })
      applyProfile(saved)
    } catch {
      setSaveError(
        'We couldn’t save that just now. Nothing was changed. Please try again.',
      )
    } finally {
      setSaving(false)
    }
  }, [applyProfile, channel, name, phone, saving, threeAm, threshold])

  return (
    <SocShell current="reach" mainClassName="box-border w-full max-w-[480px] mx-auto px-4 py-5">
        <div className="mb-1 flex items-center gap-2">
          <Phone className="w-5 h-5 text-brass" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-navy">
            How your team reaches you
          </h1>
        </div>
        <p className="mb-5 text-xs text-gray-500">
          Who we wake, for what, and how. You’re in control of this: change it
          whenever you like.
        </p>

        <OwnerMobileSection />

        {loading ? (
          <div
            className="flex items-center justify-center py-12 text-gray-400"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
            <span className="ml-2 text-sm">Loading…</span>
          </div>
        ) : loadError ? (
          <div
            className="flex flex-col gap-3 rounded-xl border border-danger/30 bg-danger-light p-4 text-sm text-danger"
            role="alert"
          >
            <div className="flex items-start gap-2">
              <AlertCircle
                className="w-4 h-4 shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <span>{loadError} Nothing is shown because nothing loaded.</span>
            </div>
            <button
              type="button"
              onClick={() => void load()}
              className="tap-min self-start rounded-lg bg-danger px-3.5 py-2 text-sm font-medium text-white hover:bg-danger/90 transition-colors"
            >
              Try again
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleSave()
            }}
            className="space-y-5"
          >
            {/* Escalation contact ─────────────────────────────────── */}
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-navy">
                Your escalation contact
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                The person and number we reach when something needs you.
              </p>

              <div className="mt-4">
                <label
                  htmlFor="esc-name"
                  className="block text-xs font-medium text-navy"
                >
                  Name
                </label>
                <input
                  id="esc-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Paula Chen"
                  className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brass/40"
                />
              </div>

              <div className="mt-3">
                <label
                  htmlFor="esc-phone"
                  className="block text-xs font-medium text-navy"
                >
                  Phone
                </label>
                <input
                  id="esc-phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value)
                    if (phoneError) setPhoneError(null)
                  }}
                  placeholder="+6421555123"
                  aria-invalid={phoneError ? true : undefined}
                  aria-describedby={
                    phoneError ? 'esc-phone-error' : 'esc-phone-help'
                  }
                  className={`mt-1 w-full rounded-xl border bg-white px-3 py-2 text-sm text-navy placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
                    phoneError
                      ? 'border-danger focus:ring-danger/40'
                      : 'border-gray-200 focus:ring-brass/40'
                  }`}
                />
                {phoneError ? (
                  <p
                    id="esc-phone-error"
                    className="mt-1 text-xs text-danger"
                    role="alert"
                  >
                    {phoneError}
                  </p>
                ) : (
                  <p id="esc-phone-help" className="mt-1 text-xs text-gray-500">
                    +64 21 555 123 format
                  </p>
                )}
              </div>
            </section>

            {/* Severity threshold ─────────────────────────────────── */}
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-baseline justify-between">
                <h2 className="text-sm font-semibold text-navy">
                  Only wake me for severity ≥
                </h2>
                <span
                  className="text-lg font-semibold text-brass tabular-nums"
                  aria-hidden="true"
                >
                  {threshold}
                </span>
              </div>
              <input
                id="esc-threshold"
                type="range"
                min={1}
                max={10}
                step={1}
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                aria-label="Only wake me for severity at or above"
                aria-valuetext={`${threshold}: ${anchor}`}
                className="mt-3 w-full accent-brass"
              />
              <div className="mt-1 flex justify-between text-[11px] text-gray-400">
                <span>1</span>
                <span>10</span>
              </div>
              <p className="mt-2 text-xs text-gray-600" aria-live="polite">
                <span className="font-medium text-navy">{threshold}:</span>{' '}
                {anchor}
              </p>
            </section>

            {/* 3am call consent ───────────────────────────────────── */}
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-navy">
                    Call me at any hour
                  </h2>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600">
                    If it’s confirmed and serious, your analyst may call this
                    number at any hour, including 3am.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={threeAm}
                  aria-label="Allow calls at any hour"
                  onClick={() => setThreeAm((v) => !v)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brass/40 ${
                    threeAm ? 'bg-brass' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      threeAm ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <p className="mt-3 text-[11px] text-gray-400">
                {threeAm
                  ? 'On: we will call for a confirmed, serious breach.'
                  : 'Off: we will not call; we’ll reach you another way.'}
              </p>
            </section>

            {/* Preferred channel ──────────────────────────────────── */}
            <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-navy">
                Preferred channel
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                How we reach you first when it isn’t urgent enough to call.
              </p>
              <fieldset className="mt-3 space-y-2">
                <legend className="sr-only">Preferred channel</legend>
                {CHANNELS.map((opt) => {
                  const selected = channel === opt.value
                  const Icon = opt.icon
                  return (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${
                        selected
                          ? 'border-brass bg-brass/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="preferred_channel"
                        value={opt.value}
                        checked={selected}
                        onChange={() => setChannel(opt.value)}
                        className="mt-0.5 accent-brass"
                      />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-navy">
                          <Icon
                            className="w-4 h-4 text-brass"
                            aria-hidden="true"
                          />
                          {opt.label}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {opt.description}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </fieldset>
            </section>

            {/* Save ───────────────────────────────────────────────── */}
            {saveError && (
              <div
                className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger-light p-3 text-sm text-danger"
                role="alert"
              >
                <AlertCircle
                  className="w-4 h-4 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>{saveError}</span>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div aria-live="polite" className="min-h-[1rem]">
                {savedAt && !saving && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-success">
                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    Saved {timeAgo(savedAt)}
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={saving}
                className="tap-min inline-flex items-center gap-1.5 rounded-lg bg-brass px-4 py-2 text-sm font-medium text-white hover:bg-brass-dark disabled:opacity-50 transition-colors"
              >
                {saving && (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                )}
                {saving ? 'Saving…' : 'Save preferences'}
              </button>
            </div>
          </form>
        )}

        {/* Who we wake (owner only, BRIEF-A10) ───────────────── */}
        <WhoWeWakeSection />

        {/* The business's clock (everyone sees it; owner or admin
            changes it, BRIEF-E7) ─────────────────────────────── */}
        <BusinessClockSection />
    </SocShell>
  )
}
